import { FC } from 'react'

interface ValidationSummaryProps {
  errors?: Record<string, unknown>;
  message?: string;
}

/**
 * Shows why a submission failed.
 *
 * Two shapes arrive from the API and both have to land. A 422 carries a
 * per-field `errors` bag. Everything else ("Please request a verification code
 * first.", "This account already has a password.") carries only a message.
 *
 * This used to return null whenever `errors` was empty, which meant a
 * message-only failure rendered nothing at all: the customer pressed the button,
 * the request failed, and the form just sat there. Every caller passes
 * `message={apiError}`, so that silently swallowed most real errors.
 */
const ValidationSummary: FC<ValidationSummaryProps> = ({
  errors,
  message,
}) => {
  const errorMessages: string[] = []

  Object.entries(errors ?? {}).forEach(([, messages]) => {
    if (Array.isArray(messages)) {
      messages.forEach((msg) => {
        errorMessages.push(msg)
      })
    } else if (typeof messages === 'object' && messages !== null) {
      Object.values(messages).forEach((msg) => {
        if (Array.isArray(msg)) {
          msg.forEach((m) => errorMessages.push(m))
        } else {
          errorMessages.push(msg as string)
        }
      })
    } else {
      errorMessages.push(messages as string)
    }
  })

  // A "one of these fields is required" rule fails on every field at once and
  // attaches the same sentence to each, so the customer is told three times to
  // enter their email, mobile or customer number. Showing a message once is
  // right whatever produced the repeat.
  const uniqueMessages = [...new Set(errorMessages.filter(Boolean))]

  const hasMessage = Boolean(message)

  if (uniqueMessages.length === 0 && !hasMessage) {
    return null
  }

  // Message on its own is the failure, not a heading over a list of them.
  if (uniqueMessages.length === 0) {
    return (
      <div className="alert alert-danger" role="alert">
        {message}
      </div>
    )
  }

  return (
    <div className="alert alert-danger" role="alert">
      <h5 className="alert-heading">
        {message || 'Validation Errors'}
      </h5>
      <ul className="error-list mb-0">
        {uniqueMessages.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  )
}

export default ValidationSummary
